import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardBody,
    Text,
    VStack,
    HStack,
    List,
    ListItem,
    Icon,
    Code,
    Tag,
    Divider,
    Flex,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
} from '@chakra-ui/react';
import { CheckIcon, CopyIcon, CheckCircleIcon, InfoIcon } from '@chakra-ui/icons';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import { useNavigate } from 'react-router-dom';
import { BRAND_NAME } from '../../../theme/brand';

const FlutterSDK = ({ data }) => {
    const [copiedSection, setCopiedSection] = useState('');
    const [innerTab, setInnerTab] = useState(0);
    const navigate = useNavigate();

    const copyToClipboard = (text, section) => {
        navigator.clipboard.writeText(text);
        setCopiedSection(section);
        setTimeout(() => setCopiedSection(''), 2000);
    };

    const CodeBlock = ({ code, section, language = 'dart' }) => {
        useEffect(() => {
            Prism.highlightAll();
        }, [code]);

        return (
            <Card bg="black" color="white" position="relative" p={4}>
                <CardBody p={0}>
                    <Button
                        position="absolute"
                        top={3}
                        right={3}
                        size="sm"
                        variant="ghost"
                        colorScheme="whiteAlpha"
                        onClick={() => copyToClipboard(code, section)}
                        zIndex={10}
                    >
                        {copiedSection === section ? <CheckIcon w={4} h={4} /> : <CopyIcon w={4} h={4} />}
                    </Button>
                    <Box
                        as="pre"
                        style={{
                            backgroundColor: 'black',
                            textShadow: 'none',
                            color: 'white',
                            padding: '0px',
                            borderRadius: '8px',
                        }}
                    >
                        <code className={`language-${language}`}>{code}</code>
                    </Box>
                </CardBody>
            </Card>
        );
    };

    // ─── Shared snippets (same for both platforms) ────────────────────────────

    const pubspecCode = `dependencies:
  xpush_flutter: ^1.1.0`;

    const sdkInitCode = `await XpushFlutter.initializeSDK(
  propertyId: "YOUR_PROPERTY_ID",
  eventKey: "YOUR_EVENT_KEY",
);`;

    const contactUpdateCode = `await XpushFlutter.contactUpdate(
  userId: phoneNumber,
);`;

    const updateEventTrackerCode = `await XpushFlutter.updateEventTrackerContact(
  phoneNumber,
);
// Example
await XpushFlutter.updateEventTrackerContact(
  "923001234567",
);`;

    const trackPageCode = `await XpushFlutter.trackEvent(
  eventName: "page_view",
  properties: {
    "screen_name": "DashboardScreen",
  },
);`;

    const trackButtonCode = `await XpushFlutter.trackEvent(
  eventName: "button_clicked",
  properties: {
    "button_name": "Transfer",
  },
);`;

    const trackCustomCode = `await XpushFlutter.trackEvent(
  eventName: "custom_event",
  properties: {
    "key": "value",
  },
);`;

    const deepLinkCode = `await XpushFlutter.configureDeepLinks({
  "deep1": "deep1",
  "deep2": "deep2",
  "deep3": "deep3",
  "pay": "pay",
  "send": "send",
});`;

    const notifCenterCode = `// Open Notification Center
await XpushFlutter.openNotifications();`;

    const unreadCountCode = `final unreadCount = await XpushFlutter.getUnreadCount();`;

    const campaignCode = `final campaignId = await XpushFlutter.getCampaignId();

await XpushFlutter.clearCampaignId();`;

    const lifecycleCode = `// Foreground
await XpushFlutter.setForeground(true);
await XpushFlutter.restartSocket();

// Background
await XpushFlutter.setForeground(false);
await XpushFlutter.stopSocketService();`;

    const logoutCode = `await XpushFlutter.stopSocketService();
// Clear your application's login/session data`;

    const isFlutterConfigured = data?.android === 1;

    const ConfigBanner = () => (
        <Box
            borderRadius="8.75px"
            border="1px solid"
            borderColor={isFlutterConfigured ? "#00c951" : "#ff8904"}
            bg={isFlutterConfigured ? "#f0fdf4" : "#fff7ed"}
            p={4}
            mb={2}
        >
            <HStack spacing={3} align="center">
                <Icon
                    as={isFlutterConfigured ? CheckCircleIcon : InfoIcon}
                    color={isFlutterConfigured ? "green.500" : "orange.500"}
                    boxSize={5}
                />
                <Box flex="1">
                    <HStack justify="space-between" align="center">
                        <Box>
                            <Text fontSize="md" fontWeight="semibold" color={isFlutterConfigured ? "green.800" : "orange.800"} mb={1}>
                                Push Notification {isFlutterConfigured ? "Configured" : "Not Configured"}
                            </Text>
                            <Text fontSize="sm" color={isFlutterConfigured ? "green.700" : "orange.700"}>
                                {isFlutterConfigured
                                    ? "Your push notification service is successfully configured and ready to use."
                                    : "Configure your push notification service to enable real-time notifications."}
                            </Text>
                        </Box>
                        <HStack spacing={3}>
                            <Button
                                bg={isFlutterConfigured ? "#00a63e" : "var(--brand-primary)"}
                                variant="solid"
                                size="sm"
                                color="white"
                                fontWeight="semibold"
                                onClick={() => navigate("/documentation/configure-push-notification", { state: { isConfigured: isFlutterConfigured } })}
                                _hover={{ opacity: 0.9 }}
                            >
                                {isFlutterConfigured ? "Re-Configure" : "Configure"}
                            </Button>
                        </HStack>
                    </HStack>
                </Box>
            </HStack>
        </Box>
    );

    // ─── Android-specific snippets ────────────────────────────────────────────

    const jitpackCode = `dependencyResolutionManagement {
  repositories {
    google()
    mavenCentral()
    maven { url 'https://jitpack.io' }
  }
}`;

    const manifestPermissionsCode = `<uses-permission android:name="android.permission.INTERNET" />
<uses-permission android:name="android.permission.WAKE_LOCK" />
<uses-permission android:name="android.permission.POST_NOTIFICATIONS" />`;

    const fcmServiceCode = `<service
    android:name="com.xpush.sdk.MyFirebaseMessagingService"
    android:exported="false">
    <intent-filter>
        <action android:name="com.google.firebase.MESSAGING_EVENT" />
    </intent-filter>
</service>`;

    const mainActivityCode = `<activity
    android:name=".MainActivity"
    android:launchMode="singleTop"
    android:exported="true">
</activity>`;

    const androidInitCode = `// Request notification permission first
await Permission.notification.request();

// Initialize SDK
await XpushFlutter.initializeSDK(
  propertyId: "YOUR_PROPERTY_ID",
  eventKey: "YOUR_EVENT_KEY",
);

// Lifecycle setup
await XpushFlutter.setForeground(true);
await XpushFlutter.restartSocket();`;

    // ─── Shared content rendered in both tabs ─────────────────────────────────

    const SharedUsage = ({ prefix }) => (
        <VStack spacing={4} align="stretch">
            {/* User Login */}
            <Card p={4} boxShadow="0 1px 3px 0 #0000001a" border="1px solid #0000001a">
                <Text fontSize="xl" fontWeight="semibold" mb={3}>User Login</Text>
                <Text color="gray.700" mb={2}>After successful login:</Text>
                <CodeBlock code={contactUpdateCode} section={`${prefix}-contact-update`} language="dart" />
                <Text color="gray.700" mt={4} mb={2}>This updates the XPush Subscriber and Event Tracker Contact Number:</Text>
                <CodeBlock code={updateEventTrackerCode} section={`${prefix}-update-et`} language="dart" />
                <Text fontSize="sm" color="gray.600" mt={3}>
                    The SDK automatically links events to the logged-in user.
                </Text>
            </Card>

            {/* Event Tracking */}
            <Card p={4} boxShadow="0 1px 3px 0 #0000001a" border="1px solid #0000001a">
                <Text fontSize="xl" fontWeight="semibold" mb={3}>Event Tracking</Text>
                <VStack spacing={4} align="stretch">
                    <Box>
                        <Text fontWeight="semibold" mb={2}>Track Page View</Text>
                        <CodeBlock code={trackPageCode} section={`${prefix}-track-page`} language="dart" />
                    </Box>
                    <Box>
                        <Text fontWeight="semibold" mb={2}>Track Button Click</Text>
                        <CodeBlock code={trackButtonCode} section={`${prefix}-track-btn`} language="dart" />
                    </Box>
                    <Box>
                        <Text fontWeight="semibold" mb={2}>Track Custom Event</Text>
                        <CodeBlock code={trackCustomCode} section={`${prefix}-track-custom`} language="dart" />
                    </Box>
                </VStack>
            </Card>

            {/* Push Click Tracking */}
            <Card p={4} boxShadow="0 1px 3px 0 #0000001a" border="1px solid #0000001a">
                <Text fontSize="xl" fontWeight="semibold" mb={3}>Push Click Tracking</Text>
                <Text color="gray.700" fontSize="sm">
                    Push click tracking is handled <strong>automatically</strong> by the SDK. When a notification
                    is opened, <Code>push_clicked</Code> is automatically sent with the <Code>messageId</Code>.
                    No additional implementation is required.
                </Text>
            </Card>

            {/* Campaign Tracking */}
            <Card p={4} boxShadow="0 1px 3px 0 #0000001a" border="1px solid #0000001a">
                <Text fontSize="xl" fontWeight="semibold" mb={3}>Campaign Tracking</Text>
                <Text color="gray.700" fontSize="sm" mb={3}>
                    Campaign IDs are managed automatically by the SDK. The SDK saves, updates, and clears
                    campaign IDs from notification payloads.
                </Text>
                <CodeBlock code={campaignCode} section={`${prefix}-campaign`} language="dart" />
                <Text fontSize="sm" color="gray.600" mt={2}>
                    Most applications do not need to call these methods directly.
                </Text>
            </Card>

            {/* Deep Linking */}
            <Card p={4} boxShadow="0 1px 3px 0 #0000001a" border="1px solid #0000001a">
                <Text fontSize="xl" fontWeight="semibold" mb={3}>Deep Linking</Text>
                <Text color="gray.700" mb={2}>Configure supported deep links:</Text>
                <CodeBlock code={deepLinkCode} section={`${prefix}-deep-link`} language="dart" />
                <Text fontSize="sm" color="gray.600" mt={2}>
                    The SDK automatically routes supported deep links.
                </Text>
            </Card>

            {/* Notification Center */}
            <Card p={4} boxShadow="0 1px 3px 0 #0000001a" border="1px solid #0000001a">
                <Text fontSize="xl" fontWeight="semibold" mb={3}>Notification Center</Text>
                <VStack spacing={3} align="stretch">
                    <Box>
                        <Text fontWeight="semibold" mb={2}>Open Notification Center</Text>
                        <CodeBlock code={notifCenterCode} section={`${prefix}-open-notif`} language="dart" />
                    </Box>
                    <Box>
                        <Text fontWeight="semibold" mb={2}>Unread Notification Count</Text>
                        <CodeBlock code={unreadCountCode} section={`${prefix}-unread`} language="dart" />
                    </Box>
                </VStack>
            </Card>

            {/* Lifecycle Handling */}
            <Card p={4} boxShadow="0 1px 3px 0 #0000001a" border="1px solid #0000001a">
                <Text fontSize="xl" fontWeight="semibold" mb={3}>Lifecycle Handling</Text>
                <Text color="gray.700" mb={2}>
                    Required for socket management, Inbox notifications, push handling, and event tracking sessions:
                </Text>
                <CodeBlock code={lifecycleCode} section={`${prefix}-lifecycle`} language="dart" />
            </Card>

            {/* Logout */}
            <Card p={4} boxShadow="0 1px 3px 0 #0000001a" border="1px solid #0000001a">
                <Text fontSize="xl" fontWeight="semibold" mb={3}>Logout</Text>
                <CodeBlock code={logoutCode} section={`${prefix}-logout`} language="dart" />
            </Card>
        </VStack>
    );

    return (
        <Box minH="100vh">
            <VStack spacing={6} align="stretch">
                <Flex justifyContent="space-between" alignItems="center">
                    <Text fontSize="2xl" fontWeight="semibold">Flutter SDK Integration</Text>
                    <Tag bg="#02569B" color="white" fontSize="xs">Flutter</Tag>
                </Flex>
                <Divider />
                <Text color="gray.700" fontSize="md">
                    Flutter plugin for integrating XPush Push Notifications, Inbox Notifications,
                    Notification Center, Deep Linking, Campaign Tracking, and Event Tracking into Flutter applications.
                </Text>

                <ConfigBanner />

                {/* Installation — same for both platforms */}
                <Card p={4} boxShadow="0 1px 3px 0 #0000001a" border="1px solid #0000001a">
                    <Text fontSize="xl" fontWeight="semibold" mb={3}>Installation</Text>
                    <Text color="gray.700" mb={2}>
                        Add the package to your <Code>pubspec.yaml</Code>:
                    </Text>
                    <CodeBlock code={pubspecCode} section="flutter-pubspec" language="yaml" />
                    <Text color="gray.700" mt={3}>Then run: <Code>flutter pub get</Code></Text>
                </Card>

                <Tabs variant="enclosed" index={innerTab} onChange={setInnerTab}>
                    <TabList
                        bg="#f1f5f9"
                        p="1"
                        borderRadius="full"
                        display="flex"
                        justifyContent="space-between"
                    >
                        <Tab
                            flex="1"
                            _selected={{ bg: 'white', boxShadow: 'sm', fontWeight: '600' }}
                            borderRadius="full"
                            fontSize="sm"
                        >
                            Android
                        </Tab>
                        <Tab
                            flex="1"
                            _selected={{ bg: 'white', boxShadow: 'sm', fontWeight: '600' }}
                            borderRadius="full"
                            fontSize="sm"
                        >
                            iOS
                        </Tab>
                    </TabList>

                    <TabPanels>
                        {/* ══════════════ ANDROID ══════════════ */}
                        <TabPanel px={0} pt={4}>
                            <VStack spacing={4} align="stretch">

                                {/* Required Configuration */}
                                <Card p={4} boxShadow="0 1px 3px 0 #0000001a" border="1px solid #0000001a">
                                    <Text fontSize="xl" fontWeight="semibold" mb={3}>Required Configuration</Text>
                                    <Text color="gray.700" fontSize="sm" mb={2}>
                                        Before using the SDK, replace all example values with your own credentials.
                                        The host application must provide:
                                    </Text>
                                    <List spacing={1}>
                                        {[
                                            'Its own Android applicationId',
                                            'Its own Firebase google-services.json',
                                            'Its own XPush propertyId',
                                            'Its own Event Tracker eventKey',
                                        ].map((item, i) => (
                                            <ListItem key={i} display="flex" alignItems="flex-start" gap={2}>
                                                <Text color="blue.500" mt={0.5}>•</Text>
                                                <Text color="gray.700" fontSize="sm">{item}</Text>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Card>

                                {/* Android Setup */}
                                <Card p={4} boxShadow="0 1px 3px 0 #0000001a" border="1px solid #0000001a">
                                    <Text fontSize="xl" fontWeight="semibold" mb={4}>Android Setup</Text>
                                    <VStack spacing={5} align="stretch">

                                        <Box>
                                            <Text fontWeight="semibold" mb={1}>Step 1 — Add Firebase Configuration</Text>
                                            <Text color="gray.700" fontSize="sm">
                                                Place your Firebase configuration file at{' '}
                                                <Code>android/app/google-services.json</Code>. The SDK does{' '}
                                                <strong>not</strong> include Firebase credentials. Your Firebase package
                                                name must match your application's package name.
                                            </Text>
                                            <Box mt={2} p={3} bg="yellow.50" border="1px solid" borderColor="yellow.200" borderRadius="md">
                                                <Text fontSize="sm" color="gray.700">
                                                    Example: <Code>applicationId "com.yourcompany.yourapp"</Code>
                                                </Text>
                                            </Box>
                                        </Box>

                                        <Divider />

                                        <Box>
                                            <Text fontWeight="semibold" mb={1}>Step 2 — Add JitPack Repository</Text>
                                            <Text color="gray.700" fontSize="sm" mb={2}>
                                                Open <Code>android/settings.gradle</Code> and add:
                                            </Text>
                                            <CodeBlock code={jitpackCode} section="and-jitpack" language="groovy" />
                                        </Box>

                                        <Divider />

                                        <Box>
                                            <Text fontWeight="semibold" mb={1}>Step 3 — Add Required Permissions</Text>
                                            <Text color="gray.700" fontSize="sm" mb={2}>
                                                Open <Code>android/app/src/main/AndroidManifest.xml</Code> and add:
                                            </Text>
                                            <CodeBlock code={manifestPermissionsCode} section="and-manifest" language="xml" />
                                            <Text fontSize="sm" color="gray.600" mt={1}>
                                                Android 13+ requires the POST_NOTIFICATIONS permission.
                                            </Text>
                                        </Box>

                                        <Divider />

                                        <Box>
                                            <Text fontWeight="semibold" mb={1}>Step 4 — Add Firebase Messaging Service</Text>
                                            <Text color="gray.700" fontSize="sm" mb={2}>
                                                Inside <Code>&lt;application&gt;</Code>:
                                            </Text>
                                            <CodeBlock code={fcmServiceCode} section="and-fcm" language="xml" />
                                        </Box>

                                        <Divider />

                                        <Box>
                                            <Text fontWeight="semibold" mb={1}>Step 5 — Configure MainActivity</Text>
                                            <Text color="gray.700" fontSize="sm" mb={2}>
                                                Ensure MainActivity uses <Code>android:launchMode="singleTop"</Code>:
                                            </Text>
                                            <CodeBlock code={mainActivityCode} section="and-main-activity" language="xml" />
                                        </Box>

                                        <Divider />

                                        <Box>
                                            <Text fontWeight="semibold" mb={1}>Step 6 — Request Notification Permission</Text>
                                            <Text color="gray.700" fontSize="sm" mb={2}>
                                                Request notification permission <strong>before</strong> SDK initialization:
                                            </Text>
                                            <CodeBlock code="await Permission.notification.request();" section="and-perm-request" language="dart" />
                                        </Box>

                                    </VStack>
                                </Card>

                                {/* SDK Initialization */}
                                <Card p={4} boxShadow="0 1px 3px 0 #0000001a" border="1px solid #0000001a">
                                    <Text fontSize="xl" fontWeight="semibold" mb={3}>SDK Initialization</Text>
                                    <Text color="gray.700" mb={2}>Initialize the SDK during app startup:</Text>
                                    <CodeBlock code={androidInitCode} section="and-sdk-init" language="dart" />
                                    <Box mt={3} p={3} bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md">
                                        <Text fontSize="sm" fontWeight="semibold" color="blue.800" mb={2}>Required Parameters</Text>
                                        <List spacing={1}>
                                            <ListItem display="flex" gap={2} alignItems="flex-start">
                                                <Code fontSize="xs" flexShrink={0}>propertyId</Code>
                                                <Text fontSize="sm" color="gray.700">XPush Property ID provided by {BRAND_NAME}</Text>
                                            </ListItem>
                                            <ListItem display="flex" gap={2} alignItems="flex-start">
                                                <Code fontSize="xs" flexShrink={0}>eventKey</Code>
                                                <Text fontSize="sm" color="gray.700">Event Tracker Key provided by {BRAND_NAME}</Text>
                                            </ListItem>
                                        </List>
                                    </Box>
                                </Card>

                                <SharedUsage prefix="and" />
                            </VStack>
                        </TabPanel>

                        {/* ══════════════ iOS ══════════════ */}
                        <TabPanel px={0} pt={4}>
                            <VStack spacing={4} align="stretch">

                                {/* SDK Initialization */}
                                <Card p={4} boxShadow="0 1px 3px 0 #0000001a" border="1px solid #0000001a">
                                    <Text fontSize="xl" fontWeight="semibold" mb={3}>SDK Initialization</Text>
                                    <Text color="gray.700" mb={2}>Initialize the SDK during app startup:</Text>
                                    <CodeBlock code={sdkInitCode} section="ios-sdk-init" language="dart" />
                                    <Box mt={3} p={3} bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md">
                                        <Text fontSize="sm" fontWeight="semibold" color="blue.800" mb={2}>Required Parameters</Text>
                                        <List spacing={1}>
                                            <ListItem display="flex" gap={2} alignItems="flex-start">
                                                <Code fontSize="xs" flexShrink={0}>propertyId</Code>
                                                <Text fontSize="sm" color="gray.700">XPush Property ID provided by {BRAND_NAME}</Text>
                                            </ListItem>
                                            <ListItem display="flex" gap={2} alignItems="flex-start">
                                                <Code fontSize="xs" flexShrink={0}>eventKey</Code>
                                                <Text fontSize="sm" color="gray.700">Event Tracker Key provided by {BRAND_NAME}</Text>
                                            </ListItem>
                                        </List>
                                    </Box>
                                </Card>

                                <SharedUsage prefix="ios" />
                            </VStack>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </VStack>
        </Box>
    );
};

export default FlutterSDK;
